import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Code, Copy, Eye, CheckCircle, CurrencyDollar } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function EmbedWidgetGenerator({ username, t }) {
  const [widgetTheme, setWidgetTheme] = useState('dark');
  const [maxProducts, setMaxProducts] = useState('4');
  const [copied, setCopied] = useState(null);

  // Use the user-facing site origin so embeds work on divispend.com (or whatever
  // domain users are visiting), not the internal preview host.
  const backendUrl = (typeof window !== 'undefined' && window.location.origin) || process.env.REACT_APP_BACKEND_URL;
  const storeUrl = `${backendUrl}/store/${username}`;

  // Iframe embed code
  const iframeCode = `<iframe src="${backendUrl}/embed-widget/${username}?theme=${widgetTheme}&limit=${maxProducts}" width="100%" height="480" frameborder="0" style="border:none;border-radius:8px;"></iframe>`;

  // Script embed code (lightweight)
  const scriptCode = `<div id="divispend-widget" data-username="${username}" data-theme="${widgetTheme}" data-limit="${maxProducts}"></div>
<script>
(function(){
  var d=document,s=d.createElement('script');
  s.src='${backendUrl}/embed-widget.js';
  s.async=true;
  d.head.appendChild(s);
})();
</script>`;

  // Simple link button
  const buttonCode = `<a href="${storeUrl}" target="_blank" style="display:inline-flex;align-items:center;gap:8px;padding:10px 20px;background:#22C55E;color:white;text-decoration:none;border-radius:4px;font-family:sans-serif;font-weight:600;font-size:14px;">
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>
  Shop on Divispend
</a>`;

  const copyCode = (code, type) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(type);
      toast.success(`${type} code copied!`);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  return (
    <Card className="border rounded-sm shadow-none" style={{ backgroundColor: t.cardBg, borderColor: t.border }} data-testid="embed-widget-card">
      <CardHeader className="p-6 pb-4">
        <CardTitle className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Cabinet Grotesk', color: t.text }}>
          <Code className="w-5 h-5 text-[#22C55E]" weight="duotone" />
          Embeddable Widget
        </CardTitle>
        <CardDescription style={{ color: t.textSecondary }}>
          Add your products to any website or landing page
        </CardDescription>
      </CardHeader>
      <CardContent className="p-6 pt-0 space-y-5">
        {/* Config */}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs tracking-[0.1em] uppercase font-semibold" style={{ color: t.textSecondary }}>Theme</Label>
            <Select value={widgetTheme} onValueChange={setWidgetTheme}>
              <SelectTrigger className="rounded-sm" style={{ backgroundColor: t.inputBg, borderColor: t.border, color: t.text }} data-testid="widget-theme-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-sm" style={{ backgroundColor: t.cardBg, borderColor: t.border }}>
                <SelectItem value="dark" style={{ color: t.text }}>Dark</SelectItem>
                <SelectItem value="light" style={{ color: t.text }}>Light</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs tracking-[0.1em] uppercase font-semibold" style={{ color: t.textSecondary }}>Products</Label>
            <Input
              type="number" min="1" max="12" value={maxProducts}
              onChange={(e) => setMaxProducts(e.target.value)}
              className="rounded-sm" style={{ backgroundColor: t.inputBg, borderColor: t.border, color: t.text }}
              data-testid="widget-limit-input"
            />
          </div>
        </div>

        {/* Embed Options */}
        <div className="space-y-3">
          {/* Script Embed */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs tracking-[0.1em] uppercase font-semibold" style={{ color: t.textSecondary }}>Script Embed (Recommended)</Label>
              <Button variant="ghost" size="sm" onClick={() => copyCode(scriptCode, 'Script')} className="h-7 px-2 text-xs" style={{ color: copied === 'Script' ? '#22C55E' : t.textSecondary }} data-testid="copy-script-embed">
                {copied === 'Script' ? <><CheckCircle className="w-3 h-3 mr-1" /> Copied</> : <><Copy className="w-3 h-3 mr-1" /> Copy</>}
              </Button>
            </div>
            <pre className="text-xs p-3 rounded-sm overflow-x-auto" style={{ backgroundColor: t.bgSecondary, color: t.textSecondary, border: `1px solid ${t.border}` }} data-testid="script-embed-code">
              {scriptCode}
            </pre>
          </div>

          {/* Iframe Embed */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs tracking-[0.1em] uppercase font-semibold" style={{ color: t.textSecondary }}>Iframe Embed</Label>
              <Button variant="ghost" size="sm" onClick={() => copyCode(iframeCode, 'Iframe')} className="h-7 px-2 text-xs" style={{ color: copied === 'Iframe' ? '#22C55E' : t.textSecondary }} data-testid="copy-iframe-embed">
                {copied === 'Iframe' ? <><CheckCircle className="w-3 h-3 mr-1" /> Copied</> : <><Copy className="w-3 h-3 mr-1" /> Copy</>}
              </Button>
            </div>
            <pre className="text-xs p-3 rounded-sm overflow-x-auto" style={{ backgroundColor: t.bgSecondary, color: t.textSecondary, border: `1px solid ${t.border}` }} data-testid="iframe-embed-code">
              {iframeCode}
            </pre>
          </div>

          {/* Button Embed */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs tracking-[0.1em] uppercase font-semibold" style={{ color: t.textSecondary }}>Simple Button</Label>
              <Button variant="ghost" size="sm" onClick={() => copyCode(buttonCode, 'Button')} className="h-7 px-2 text-xs" style={{ color: copied === 'Button' ? '#22C55E' : t.textSecondary }} data-testid="copy-button-embed">
                {copied === 'Button' ? <><CheckCircle className="w-3 h-3 mr-1" /> Copied</> : <><Copy className="w-3 h-3 mr-1" /> Copy</>}
              </Button>
            </div>
            <pre className="text-xs p-3 rounded-sm overflow-x-auto" style={{ backgroundColor: t.bgSecondary, color: t.textSecondary, border: `1px solid ${t.border}` }} data-testid="button-embed-code">
              {buttonCode}
            </pre>
            {/* Preview */}
            <div className="mt-2 p-3 rounded-sm flex items-center justify-center" style={{ backgroundColor: t.bgSecondary, border: `1px dashed ${t.border}` }}>
              <a href={storeUrl} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 20px', background: '#22C55E', color: 'white', textDecoration: 'none', borderRadius: '4px', fontWeight: 600, fontSize: '14px' }}>
                <CurrencyDollar className="w-4 h-4" /> Shop on Divispend
              </a>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
